import { z } from 'zod';
import { hexColorSchema } from './tenancy.js';
export const ORG_STATUSES = ['onboarding', 'active', 'suspended', 'closed'];
export const STAFF_ROLES = ['org_admin', 'unit_manager', 'doctor', 'nurse', 'sales', 'reception', 'finance'];
export const platformOrganizationSummarySchema = z.object({
    id: z.uuid(),
    slug: z.string(),
    tradeName: z.string(),
    legalName: z.string(),
    cnpj: z.string(),
    status: z.enum(ORG_STATUSES),
    plan: z.string(),
    createdAt: z.string(),
    portalHost: z.string(),
    primaryColor: z.string(),
    membersCount: z.number().int(),
    contactsCount: z.number().int(),
});
export const platformMemberSchema = z.object({
    userId: z.uuid(),
    name: z.string(),
    email: z.string(),
    roles: z.array(z.enum(STAFF_ROLES)),
    status: z.string(),
    mfaEnrolled: z.boolean(),
    lastLoginAt: z.string().nullable(),
});
export const platformOrganizationDetailSchema = platformOrganizationSummarySchema.extend({
    branding: z.object({
        displayName: z.string(),
        colorPrimary: hexColorSchema,
        colorAccent: hexColorSchema.nullable(),
        aiDisplayName: z.string(),
        logoUrl: z.string().nullable(),
        version: z.number().int(),
    }),
    domains: z.array(z.object({ domain: z.string(), kind: z.string(), status: z.string(), isPrimary: z.boolean() })),
    aiEnabled: z.boolean(),
    members: z.array(platformMemberSchema),
});
export const updateOrganizationStatusSchema = z.object({ status: z.enum(['active', 'suspended']) });
export const updateBrandingSchema = z.object({
    displayName: z.string().trim().min(2, 'Nome com ao menos 2 caracteres').max(80),
    colorPrimary: hexColorSchema,
    colorAccent: hexColorSchema.nullable(),
    aiDisplayName: z.string().trim().min(2).max(60),
});
export const addMemberSchema = z.object({
    email: z.email('E-mail inválido').max(254),
    name: z.string().trim().min(2, 'Informe o nome').max(120),
    role: z.enum(STAFF_ROLES),
});
export const addMemberResponseSchema = z.object({
    userId: z.uuid(),
    created: z.boolean(),
    /** Só quando o usuário foi criado agora: exibida uma única vez para o administrador repassar. */
    temporaryPassword: z.string().optional(),
});
export const slugCheckResponseSchema = z.object({
    slug: z.string(),
    available: z.boolean(),
    message: z.string().optional(),
});
/** Console → Configurações → E-mail (Brevo). A chave nunca volta para o navegador: só o final dela. */
export const emailSettingsUpdateSchema = z.object({
    apiKey: z.string().trim().max(200).optional(), // vazio = mantém a atual
    from: z.string().trim().toLowerCase().max(254).pipe(z.email('Informe um e-mail válido')),
});
export const emailSettingsSchema = z.object({
    configured: z.boolean(),
    source: z.enum(['console', 'env', 'none']),
    keyHint: z.string().nullable(), // ex.: "xkeysib-…9f2a"
    from: z.string().nullable(),
    account: z.string().nullable(), // conta Brevo da chave (e-mail/empresa)
    updatedAt: z.string().nullable(),
    updatedBy: z.string().nullable(),
    domain: z.object({
        name: z.string(),
        status: z.enum(['authenticated', 'pending', 'not_added', 'unknown']),
        records: z.array(z.object({ name: z.string(), type: z.string(), host: z.string(), value: z.string(), ok: z.boolean() })),
    }).nullable(),
});
export const emailTestResponseSchema = z.object({ sentTo: z.string() });
/** Console → Configurações → WhatsApp (Evolution API). A chave nunca volta para o navegador. */
export const evolutionSettingsUpdateSchema = z.object({
    url: z.url('Informe a URL completa, ex.: https://evolution.suaempresa.com.br').max(300),
    apiKey: z.string().trim().max(300).optional(), // vazio = mantém a atual
});
export const evolutionSettingsSchema = z.object({
    configured: z.boolean(),
    source: z.enum(['console', 'env', 'none']),
    url: z.string().nullable(),
    keyHint: z.string().nullable(),
    /** resultado da checagem feita agora (lista de instâncias com a chave global) */
    reachable: z.boolean().nullable(),
    instances: z.number().int().nullable(),
    error: z.string().nullable(),
    updatedAt: z.string().nullable(),
    updatedBy: z.string().nullable(),
});
/** Números de WhatsApp da clínica (instâncias da Evolution). */
export const whatsappChannelSchema = z.object({
    id: z.uuid(),
    displayName: z.string(),
    instanceName: z.string().nullable(),
    provider: z.string(),
    status: z.enum(['pending', 'connected', 'disconnected', 'banned']),
    phoneLast4: z.string().nullable(),
    createdAt: z.string(),
});
export const createWhatsappChannelSchema = z.object({ displayName: z.string().trim().min(2, 'Dê um nome ao número').max(60) });
export const whatsappQrSchema = z.object({
    channel: whatsappChannelSchema,
    /** data URL do QR (null quando já conectado) e código de pareamento, se a Evolution devolver */
    qr: z.object({ base64: z.string().nullable(), pairingCode: z.string().nullable() }),
});
// ------------------------------------------------------------ Instagram (app da Meta)
export const instagramAppSettingsSchema = z.object({
    appId: z.string().trim().regex(/^\d{6,25}$/, 'O ID do app do Instagram tem só números'),
    /** vazio = mantém o segredo já salvo */
    appSecret: z.string().trim().max(200).optional().transform((v) => v || undefined),
});
//# sourceMappingURL=platform.js.map